import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';

import Button from '@/components/Button';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';

import {
  filterRecipientIds,
  useBroadcastRecipients,
} from '../api/useBroadcastRecipients';
import { useWorkerSearch } from '../api/useWorkerSearch';
import { useWorkersByIds, type WorkerChip } from '../api/useWorkersByIds';
import { useSupabaseClient } from '../supabase/SupabaseProvider';
import type { Broadcast } from '../types';

interface BroadcastRecipientsProps {
  broadcast: Broadcast;
}

const searchInputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid #d1d5db',
  borderRadius: 6,
  padding: '8px 10px',
  fontSize: 13,
  outline: 'none',
  margin: '0 0 12px',
  boxSizing: 'border-box',
};

const metaStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#6b7280',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 10px',
  borderBottom: '1px solid #f3f4f6',
};

const rowInfoStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  flex: 1,
  minWidth: 0,
};

const nameStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: '#111827',
};

const profileLinkStyle: React.CSSProperties = {
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  fontSize: 13,
  color: '#6b7280',
};

const errorStyle: React.CSSProperties = {
  fontSize: 12,
  padding: '8px 10px',
  borderRadius: 6,
  background: '#fef2f2',
  color: '#b91c1c',
  marginBottom: 12,
};

const loadMoreButtonStyle: React.CSSProperties = {
  appearance: 'none',
  border: '1px solid #d1d5db',
  background: '#fff',
  color: '#374151',
  fontSize: 12,
  fontWeight: 600,
  padding: '6px 12px',
  borderRadius: 6,
  margin: '8px 10px',
};

const scrollContainerStyle: React.CSSProperties = {
  maxHeight: '60vh',
  overflowY: 'auto',
  border: '1px solid #e5e7eb',
  borderRadius: 6,
};

function workerName(
  worker: WorkerChip | null | undefined,
  fallback: string,
): string {
  const first = worker?.user?.first_name ?? '';
  const last = worker?.user?.last_name ?? '';
  const composed = `${first} ${last}`.trim();
  return composed || worker?.user?.email || fallback;
}

function RecipientRow({
  id,
  worker,
}: {
  id: number;
  worker: WorkerChip | null | undefined;
}) {
  return (
    <div style={rowStyle}>
      <div style={rowInfoStyle}>
        <div style={nameStyle}>{workerName(worker, `Worker #${id}`)}</div>
        <div style={metaStyle}>{worker?.user?.email ?? ''}</div>
      </div>
      <a
        href={`/workers_v2/${id}`}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Open worker profile"
        data-testid="recipientProfileLink"
        onClick={(e) => e.stopPropagation()}
        style={profileLinkStyle}
      >
        <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
      </a>
    </div>
  );
}

export function BroadcastRecipients({ broadcast }: BroadcastRecipientsProps) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [membershipIds, setMembershipIds] = useState<number[] | null>(null);
  const [membershipLoading, setMembershipLoading] = useState(false);
  const [membershipError, setMembershipError] = useState<string | null>(null);
  // Bumped whenever the active query changes (and cleared) so a
  // slow-resolving filterRecipientIds response for an old query can't
  // clobber the state of whatever query is current by the time it settles.
  const membershipGenRef = useRef(0);

  const client = useSupabaseClient();
  const { workerIds, total, loading, error, hasMore, loadMore } =
    useBroadcastRecipients(broadcast.id);
  const { workers } = useWorkersByIds(open ? workerIds : []);

  const trimmedQuery = searchQuery.trim();
  const isQueryActive = trimmedQuery.length >= 2;

  const {
    results: searchResults,
    loading: searchLoading,
    error: searchError,
  } = useWorkerSearch(isQueryActive ? searchQuery : '');

  useEffect(() => {
    // Every exit path — including this cleanup, which fires on the next
    // effect run AND on unmount (e.g. switching broadcasts remounts this
    // component via its `key`) — bumps the generation, so a promise that
    // resolves after either can never pass the staleness check below.
    if (!isQueryActive) {
      membershipGenRef.current += 1;
      setMembershipIds(null);
      setMembershipError(null);
      setMembershipLoading(false);
      return () => {
        membershipGenRef.current += 1;
      };
    }
    if (searchLoading || !client) {
      membershipGenRef.current += 1;
      return () => {
        membershipGenRef.current += 1;
      };
    }

    const generation = ++membershipGenRef.current;

    if (searchResults.length === 0) {
      setMembershipIds([]);
      setMembershipError(null);
      setMembershipLoading(false);
      return () => {
        membershipGenRef.current += 1;
      };
    }

    setMembershipLoading(true);
    setMembershipError(null);
    void (async () => {
      try {
        const ids = await filterRecipientIds(
          client,
          broadcast.id,
          searchResults.map((w) => w.id),
        );
        if (membershipGenRef.current !== generation) return; // stale
        setMembershipIds(ids);
      } catch {
        if (membershipGenRef.current !== generation) return; // stale
        setMembershipError('Unable to search recipients right now.');
      } finally {
        if (membershipGenRef.current === generation) {
          setMembershipLoading(false);
        }
      }
    })();

    return () => {
      membershipGenRef.current += 1;
    };
  }, [isQueryActive, searchLoading, searchResults, client, broadcast.id]);

  const matchedRecipients = useMemo(() => {
    if (!membershipIds || membershipIds.length === 0) return [];
    const matchedSet = new Set(membershipIds);
    return searchResults.filter((w) => matchedSet.has(w.id));
  }, [membershipIds, searchResults]);

  const displayCount = broadcast.recipient_count ?? total;
  const titleText = `Recipients${displayCount != null ? ` (${displayCount})` : ''}`;

  const handleClose = useCallback(() => {
    setOpen(false);
    setSearchQuery('');
  }, []);

  const searching = isQueryActive && (searchLoading || membershipLoading);
  const activeError = isQueryActive ? (membershipError ?? searchError) : null;
  const noMatches =
    isQueryActive &&
    !searching &&
    !activeError &&
    membershipIds !== null &&
    matchedRecipients.length === 0;

  return (
    <>
      <Button
        a11yLabel="View workers"
        appearance="outline"
        label="View workers"
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      />

      {open ? (
        <Modal
          displayFlex
          size="sm"
          title={titleText}
          onRequestClose={handleClose}
          wrapperBackground
        >
          <Card>
            <Card.Section>
              <div data-testid="broadcastRecipients">
                <input
                  type="search"
                  data-testid="recipientSearchInput"
                  placeholder="Search all recipients by name or email…"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={searchInputStyle}
                  autoFocus
                />

                {!isQueryActive ? (
                  <div style={{ ...metaStyle, marginBottom: 8 }}>
                    {broadcast.recipient_count != null
                      ? `${workerIds.length} of ${broadcast.recipient_count} loaded`
                      : `${workerIds.length} loaded`}
                  </div>
                ) : null}

                <div style={scrollContainerStyle}>
                  {isQueryActive ? (
                    searching ? (
                      <div style={{ padding: '12px 10px', ...metaStyle }}>
                        Searching…
                      </div>
                    ) : activeError ? (
                      <div style={{ ...errorStyle, margin: 10 }}>
                        {activeError}
                      </div>
                    ) : noMatches ? (
                      <div style={{ padding: '12px 10px', ...metaStyle }}>
                        No recipients match
                      </div>
                    ) : (
                      matchedRecipients.map((worker) => (
                        <RecipientRow
                          key={worker.id}
                          id={worker.id}
                          worker={worker}
                        />
                      ))
                    )
                  ) : loading && workerIds.length === 0 ? (
                    <div style={{ padding: 12 }}>
                      <LoadingState />
                    </div>
                  ) : workerIds.length === 0 ? (
                    error ? (
                      <div style={{ ...errorStyle, margin: 10 }}>{error}</div>
                    ) : (
                      <div style={{ padding: '12px 10px', ...metaStyle }}>
                        No recipients yet.
                      </div>
                    )
                  ) : (
                    <>
                      {workerIds.map((id) => (
                        <RecipientRow key={id} id={id} worker={workers[id]} />
                      ))}
                      {error ? (
                        <div style={{ ...errorStyle, margin: 10 }}>{error}</div>
                      ) : null}
                    </>
                  )}

                  {!isQueryActive && hasMore ? (
                    <button
                      type="button"
                      onClick={() => void loadMore()}
                      disabled={loading}
                      style={{
                        ...loadMoreButtonStyle,
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? 'wait' : 'pointer',
                      }}
                    >
                      {loading ? 'Loading…' : 'Load more'}
                    </button>
                  ) : null}
                </div>
              </div>
            </Card.Section>
          </Card>
        </Modal>
      ) : null}
    </>
  );
}

export default BroadcastRecipients;
